import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { UpdateExpeditionNameRequestInDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';


interface ComponentProps {
  expeditionId?: string;
  applicationId?: string;
  currentName?: string;
  onContinue: (name: string) => void;
}

interface UpdateExpeditionNameForm {
  name: string;
}

export const useCreateUpdateExpeditionNameSidebar = () => {
  const [visible, setVisible] = useState(false);

  const UpdateExpeditionNameSidebar: React.FC<ComponentProps> = ({
                                                                   onContinue,
                                                                   expeditionId,
                                                                   applicationId,
                                                                   currentName,
                                                                 }) => {
    const { tr } = useTranslationLgs();
    const { updateExpeditionName } = useContext(ApiContext);
    const [isLoading, setIsLoading] = useState(false);

    const save = (data: UpdateExpeditionNameForm) => {
      setIsLoading(true);
      if (expeditionId) {
        const request: UpdateExpeditionNameRequestInDTO = {
          name: data.name,
        };

        updateExpeditionName(
          expeditionId,
          request,
          () => {
            onContinue(data.name);
            setVisible(false);
            setIsLoading(false);
          },
          null,
          {
            onError: (_error, showStandardError) => {
              showStandardError();
              setIsLoading(false);
            },
          },
        );
      } else {
        onContinue(data.name);
        setVisible(false);
        setIsLoading(false);
      }

    };

    const schema = yup.object().shape({
      name: yup.string().max(255).optional(),
    });

    const form = useForm<UpdateExpeditionNameForm>(
      schema,
      { name: currentName },
      (d) => save(d),
      false,
      false,
    );

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('UpdateExpeditionNameSidebar.updateExpeditionName', 'Update Expedition Name')}
          loading={isLoading}
          dataTestIdContinue="expeditionInfo-expeditionNameSave"
          dataTestIdBack="expeditionInfo-expeditionNameClose"
        >
          <div className={'flex flex-col gap-4'}>
            {applicationId && (
              <VerticalFieldAndValueWithIcon
                label={tr('UpdateExpeditionNameSidebar.forExpedition', 'For expedition')}
                valueText={applicationId}
                iconPath={'/images/icons/ringil3/forExpedition.svg'}
              />
            )}

            <Field label={tr('UpdateExpeditionNameSidebar.name', 'Expedition name')}>
              <RingilTextArea
                value={form.values.name}
                onChange={value =>
                  form.setFieldValue('name', value)}
                placeholder={tr('UpdateExpeditionNameSidebar.enterName', 'Enter expedition name')}
                maxLength={255}
                rows={2}
                dataTestId="expeditionInfo-expeditionNameInput"
              />
            </Field>
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { UpdateExpeditionNameSidebar, setVisible, visible };
};